import { PatientAppStatusColor } from '../core/models/patient-app-status-color';
import { ChipColour } from '../shared/chip/chip.component';
import { safeParseJson } from '../shared/util/helper.util';

export interface PatientAppTimelineEvent {
  event?: string;
  date?: string;
  createdAt?: string;
  timestamp?: string;
}

function toEventDate(timelineEvent: PatientAppTimelineEvent): string | null {
  return timelineEvent.date || timelineEvent.createdAt || timelineEvent.timestamp || null;
}

function getEventTime(timelineEvent: PatientAppTimelineEvent): number {
  return new Date(toEventDate(timelineEvent) || 0).getTime();
}

function sortOldestFirst(
  timelineEvents: PatientAppTimelineEvent[]
): PatientAppTimelineEvent[] {
  return timelineEvents
    .slice()
    .sort((first, second) => getEventTime(first) - getEventTime(second));
}

function sortNewestFirst(
  timelineEvents: PatientAppTimelineEvent[]
): PatientAppTimelineEvent[] {
  return timelineEvents
    .slice()
    .sort((first, second) => getEventTime(second) - getEventTime(first));
}

export function getLatestTimelineActivity(
  timeline: PatientAppTimelineEvent[]
): PatientAppTimelineEvent {
  if (!timeline || timeline.length === 0) {
    return { event: 'No activity' };
  }
  return sortNewestFirst(timeline)[0] || { event: 'No activity' };
}

function getPatientAppBadgeColour(patientAppStatus: string | null): string {
  return patientAppStatus
    ? PatientAppStatusColor[patientAppStatus.toLowerCase()] || 'gray'
    : 'gray';
}

export function getPatientAppBadgeClass(patientAppStatus: string | null): string {
  return `badge-${getPatientAppBadgeColour(patientAppStatus)}`;
}

// csi-chip carries a narrower palette than the badge classes, so the two purely cosmetic splits
// collapse: rose (expired) joins red, and green-vibrant (re-enrolled) joins green.
const CHIP_COLOUR_BY_BADGE_COLOUR: { [badgeColour: string]: ChipColour } = {
  gray: 'grey-no-outline',
  blue: 'blue-no-outline',
  green: 'green-no-outline',
  'green-vibrant': 'green-no-outline',
  orange: 'orange',
  red: 'red-no-outline',
  rose: 'red-no-outline'
};

export function getPatientAppChipColour(patientAppStatus: string | null): ChipColour {
  return (
    CHIP_COLOUR_BY_BADGE_COLOUR[getPatientAppBadgeColour(patientAppStatus)] || 'grey-no-outline'
  );
}

export function getTimelineEventDate(
  timeline: PatientAppTimelineEvent[],
  eventNames: string[]
): string | null {
  if (!timeline || timeline.length === 0) {
    return null;
  }
  const matchedEvents = timeline.filter(
    timelineEvent =>
      timelineEvent.event &&
      eventNames.some(eventName => eventName.toLowerCase() === timelineEvent.event.toLowerCase())
  );
  if (matchedEvents.length === 0) {
    return null;
  }
  const newestMatch = sortNewestFirst(matchedEvents)[0];
  return newestMatch ? toEventDate(newestMatch) : null;
}

// Lifecycle stages a patient app enrollment moves through. Deliberately a closed set — an
// unrecognised status falls back to 'invited' only when a timeline proves an invite happened.
export type PatientAppStage = 'notEnrolled' | 'invited' | 'active' | 'paused' | 'expired';

export interface PatientAppState {
  stage: PatientAppStage;
  statusKey: string;
  currentStatusDate: string | null;
  statusLabel: string;
  badgeClass: string;
  isEnrolled: boolean;
  invitedDate: string | null;
  registeredDate: string | null;
  lastActivityDate: string | null;
}

const STAGE_BY_STATUS: { [status: string]: PatientAppStage } = {
  invited: 'invited',
  registered: 'active',
  active: 'active',
  paused: 'paused',
  expired: 'expired'
};

// Single source of truth for patient app state. The record column, the header strip and anything
// else that needs this must call it rather than re-parsing patientAppEnrollmentConfig themselves.
export function buildPatientAppState(patient: any): PatientAppState {
  const enrollmentConfig = patient ? safeParseJson(patient.patientAppEnrollmentConfig) : null;
  const status = enrollmentConfig ? enrollmentConfig.status : null;
  const timeline = enrollmentConfig ? enrollmentConfig.timeline : null;

  const invitedDate = getTimelineEventDate(timeline, ['invited']);
  const registeredDate = getTimelineEventDate(timeline, ['registered']);
  const latestActivity = getLatestTimelineActivity(timeline);
  const normalizedStatus = status ? String(status).toLowerCase() : '';

  let stage: PatientAppStage = STAGE_BY_STATUS[normalizedStatus];
  if (!stage) {
    stage = invitedDate ? 'invited' : 'notEnrolled';
  }

  return {
    stage: stage,
    statusKey: normalizedStatus,
    // Timeline events are named after the statuses themselves, so the current status can look up
    // its own date without a per-status branch.
    currentStatusDate: normalizedStatus
      ? getTimelineEventDate(timeline, [normalizedStatus])
      : null,
    statusLabel: status || 'Not enrolled',
    badgeClass: getPatientAppBadgeClass(status),
    isEnrolled: stage !== 'notEnrolled',
    invitedDate: invitedDate,
    registeredDate: registeredDate,
    lastActivityDate: latestActivity ? toEventDate(latestActivity) : null
  };
}

export interface PatientAppCycleEvent {
  eventLabel: string;
  date: string | null;
}

export interface PatientAppEnrolmentCycle {
  cycleNumber: number;
  isCurrent: boolean;
  startDate: string | null;
  endDate: string | null;
  events: PatientAppCycleEvent[];
}

export interface PatientAppHistory {
  cycles: PatientAppEnrolmentCycle[];
  enrolmentCount: number;
}

// A cycle begins whenever the patient is put back into the app: the first invite, or a re-enrol
// from the assessment. Endings ('paused', 'expired', 'invitation expired') close a cycle rather
// than starting one, so they stay inside the cycle they belong to.
const ENROLMENT_START_EVENTS = ['invited', 're-enrolled'];

// The app lifecycle is cyclical, not linear: a patient can be invited, lapse, and be invited
// again. Splitting on every invite is what makes a re-enrolment visible — buildPatientAppState
// keeps only the newest date per event name, so it cannot show one.
export function buildPatientAppHistory(patient: any): PatientAppHistory {
  const enrollmentConfig = patient ? safeParseJson(patient.patientAppEnrollmentConfig) : null;
  const timeline: PatientAppTimelineEvent[] =
    enrollmentConfig && enrollmentConfig.timeline ? enrollmentConfig.timeline : [];

  if (timeline.length === 0) {
    return { cycles: [], enrolmentCount: 0 };
  }

  const oldestFirst = sortOldestFirst(timeline);
  const cycles: PatientAppEnrolmentCycle[] = [];

  oldestFirst.forEach(timelineEvent => {
    const eventLabel = timelineEvent.event ? String(timelineEvent.event).toLowerCase() : '';
    const isEnrolmentStart = ENROLMENT_START_EVENTS.indexOf(eventLabel) !== -1;
    const currentCycle = cycles[cycles.length - 1];

    if (isEnrolmentStart || !currentCycle) {
      cycles.push({
        cycleNumber: cycles.length + 1,
        isCurrent: false,
        startDate: toEventDate(timelineEvent),
        endDate: toEventDate(timelineEvent),
        events: []
      });
    }

    const openCycle = cycles[cycles.length - 1];
    openCycle.events.push({ eventLabel: eventLabel, date: toEventDate(timelineEvent) });
    openCycle.endDate = toEventDate(timelineEvent);
  });

  if (cycles.length > 0) {
    cycles[cycles.length - 1].isCurrent = true;
  }

  return {
    cycles: cycles.slice().reverse(),
    enrolmentCount: cycles.filter(cycle =>
      cycle.events.some(
        cycleEvent => ENROLMENT_START_EVENTS.indexOf(cycleEvent.eventLabel) !== -1
      )
    ).length
  };
}
